// AuthContext.tsx - Authentication context using app_ tables structure
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Types
// ============================================================================

export interface PagePermission {
  view: boolean;
  edit: boolean;
  export: boolean;
}

export interface UserPermissions {
  [pageCode: string]: PagePermission;
}

export interface AuthUser {
  user_key: number;
  user_guid?: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role_key: number;
  role_code: string;
  role_name: string;
  supplier_key: number | null;
  insurer_keys: number[];
  workshop_keys: number[];
  permissions: UserPermissions;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  hasPageAccess: (pageCode: string) => boolean;
  canEdit: (pageCode: string) => boolean;
  canExport: (pageCode: string) => boolean;
  getDataFilters: () => DataFilters;
  refreshUser: () => Promise<void>;
}

export interface DataFilters {
  insurer_keys: number[] | null;
  supplier_key: number | null;
  workshop_keys: number[] | null;
  is_admin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Storage Keys
// ============================================================================

const TOKEN_KEY = 'xnuup_token';
const USER_KEY = 'xnuup_user';

// ============================================================================
// Page Definitions - All available pages in the system
// ============================================================================

export const ALL_PAGES = [
  { code: 'dashboard', name: 'Dashboard', category: 'analytics', description: 'Main dashboard overview' },
  { code: 'operations', name: 'Operations', category: 'analytics', description: 'Operational metrics and KPIs' },
  { code: 'parts', name: 'Parts', category: 'analytics', description: 'Parts analytics and tracking' },
  { code: 'suppliers', name: 'Suppliers', category: 'analytics', description: 'Supplier performance analysis' },
  { code: 'workshops', name: 'Workshops', category: 'analytics', description: 'Workshop analytics' },
  { code: 'performance', name: 'Performance', category: 'analytics', description: 'Performance metrics' },
  { code: 'patterns', name: 'Patterns', category: 'analytics', description: 'Pattern analysis and insights' },
  { code: 'claims', name: 'Claims', category: 'data', description: 'Claims management and lookup' },
  { code: 'orders', name: 'Orders', category: 'data', description: 'Order tracking and details' },
  { code: 'reports', name: 'Reports', category: 'reporting', description: 'Report generation and export' },
  { code: 'shareholders', name: 'Shareholders', category: 'executive', description: 'Executive KPIs and financials' },
  { code: 'insurer-profile', name: 'Insurer Profile', category: 'profiles', description: 'Insurer analytics dashboard' },
  { code: 'supplier-profile', name: 'Supplier Profile', category: 'profiles', description: 'Supplier dashboard' },
  { code: 'workshop-profile', name: 'Workshop Profile', category: 'profiles', description: 'Workshop dashboard' },
  { code: 'eda', name: 'EDA', category: 'admin', description: 'Exploratory Data Analysis tools' },
  { code: 'etl', name: 'ETL Monitor', category: 'admin', description: 'ETL pipeline monitoring' },
  { code: 'users', name: 'User Admin', category: 'admin', description: 'User management' },
] as const;

export type PageCode = typeof ALL_PAGES[number]['code'];

// ============================================================================
// Role Definitions with Default Page Access
// ============================================================================

export const ROLE_DEFINITIONS: Record<string, {
  name: string;
  description: string;
  icon: string;
  color: string;
  defaultPages: string[];
}> = {
  app_admin: {
    name: 'System Administrator',
    description: 'Full access to all features and data',
    icon: '👑',
    color: 'purple',
    defaultPages: ALL_PAGES.map(p => p.code),
  },
  shareholder: {
    name: 'Shareholder',
    description: 'Executive view with financial KPIs and reports',
    icon: '💼',
    color: 'indigo',
    defaultPages: ['dashboard', 'operations', 'claims', 'orders', 'parts', 'suppliers', 'workshops', 'performance', 'patterns', 'reports', 'shareholders', 'workshop-profile'],
  },
  insurer_admin: {
    name: 'Insurer Admin',
    description: 'Full access for assigned insurers',
    icon: '🏢',
    color: 'blue',
    defaultPages: ['dashboard', 'operations', 'parts', 'suppliers', 'workshops', 'performance', 'claims', 'orders', 'patterns', 'reports', 'insurer-profile'],
  },
  insurer_analyst: {
    name: 'Insurer Analyst',
    description: 'Read-only analytics for assigned insurers',
    icon: '📊',
    color: 'cyan',
    defaultPages: ['dashboard', 'parts', 'claims', 'orders', 'reports', 'insurer-profile'],
  },
  supplier_user: {
    name: 'Supplier User',
    description: 'Access to own supplier data only',
    icon: '🚚',
    color: 'green',
    defaultPages: ['dashboard', 'operations', 'claims', 'orders', 'parts', 'reports', 'supplier-profile'],
  },
  workshop_user: {
    name: 'Workshop User',
    description: 'Access to own workshop data only',
    icon: '🔧',
    color: 'orange',
    defaultPages: ['dashboard', 'operations', 'claims', 'parts', 'reports', 'workshop-profile'],
  },
};

export type RoleCode = keyof typeof ROLE_DEFINITIONS;

// Admin-only pages (cannot be granted to non-admins)
export const ADMIN_ONLY_PAGES = ['eda', 'etl', 'users'];

// ============================================================================
// Helper Functions
// ============================================================================

export const getDefaultPermissions = (roleCode: string): UserPermissions => {
  const roleDef = ROLE_DEFINITIONS[roleCode];
  if (!roleDef) return {};
  
  return roleDef.defaultPages.reduce((acc, pageCode) => {
    acc[pageCode] = {
      view: true,
      edit: roleCode === 'app_admin' || roleCode === 'insurer_admin' ||
            (roleCode === 'supplier_user' && pageCode === 'supplier-profile') ||
            (roleCode === 'workshop_user' && pageCode === 'workshop-profile'),
      export: true
    };
    return acc;
  }, {} as UserPermissions);
};

export const getRoleColor = (roleCode: string): string => {
  return ROLE_DEFINITIONS[roleCode]?.color || 'gray';
};

export const getRoleIcon = (roleCode: string): string => {
  return ROLE_DEFINITIONS[roleCode]?.icon || '👤';
};

export const getRoleName = (roleCode: string): string => {
  return ROLE_DEFINITIONS[roleCode]?.name || roleCode;
};

// ============================================================================
// Auth Provider
// ============================================================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from storage
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        // Ensure permissions exist
        if (!parsedUser.permissions || Object.keys(parsedUser.permissions).length === 0) {
          parsedUser.permissions = getDefaultPermissions(parsedUser.role_code);
        }
        setUser(parsedUser);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Login
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;

        // Ensure permissions exist
        if (!userData.permissions || Object.keys(userData.permissions).length === 0) {
          userData.permissions = getDefaultPermissions(userData.role_code);
        }
        
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        
        setToken(newToken);
        setUser(userData);
        
        return { success: true };
      }

      return { success: false, error: data.error || 'Login failed' };
    } catch (error: any) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }, []);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/users/permissions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && data.data && user) {
        const updatedUser = { ...user, permissions: data.data };
        setUser(updatedUser);
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [token, user]);

  // Check page access
  const hasPageAccess = useCallback((pageCode: string): boolean => {
    if (!user) return false;
    
    // Admin has universal access
    if (user.role_code === 'app_admin') return true;
    
    // Admin-only pages restricted to app_admin
    if (ADMIN_ONLY_PAGES.includes(pageCode)) {
      return false;
    }
    
    // Check user's permissions (from database or defaults)
    if (user.permissions && user.permissions[pageCode]?.view === true) {
      return true;
    }
    
    // Special profile pages based on user type
    if (pageCode === 'supplier-profile' && user.supplier_key) return true;
    if (pageCode === 'workshop-profile' && user.workshop_keys && user.workshop_keys.length > 0) return true;
    if (pageCode === 'insurer-profile' && user.insurer_keys && user.insurer_keys.length > 0) return true;
    
    return false;
  }, [user]);

  // Check edit permission
  const canEdit = useCallback((pageCode: string): boolean => {
    if (!user) return false;
    if (user.role_code === 'app_admin') return true;
    return user.permissions?.[pageCode]?.edit === true;
  }, [user]);

  // Check export permission
  const canExport = useCallback((pageCode: string): boolean => {
    if (!user) return false;
    if (user.role_code === 'app_admin') return true;
    return user.permissions?.[pageCode]?.export === true;
  }, [user]);

  // Get data filters based on role
  const getDataFilters = useCallback((): DataFilters => {
    if (!user) {
      return { insurer_keys: null, supplier_key: null, workshop_keys: null, is_admin: false };
    }

    // Admin and shareholder see all data
    if (user.role_code === 'app_admin' || user.role_code === 'shareholder') {
      return { insurer_keys: null, supplier_key: null, workshop_keys: null, is_admin: true };
    }

    return {
      insurer_keys: user.insurer_keys && user.insurer_keys.length > 0 ? user.insurer_keys : null,
      supplier_key: user.supplier_key,
      workshop_keys: user.workshop_keys && user.workshop_keys.length > 0 ? user.workshop_keys : null,
      is_admin: false,
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    hasPageAccess,
    canEdit,
    canExport,
    getDataFilters,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hooks
// ============================================================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook to get authorized fetch function
export const useAuthFetch = () => {
  const { token, logout } = useAuth();

  return useCallback(async (url: string, options: RequestInit = {}) => {
    const headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      logout();
      throw new Error('Session expired');
    }

    return response;
  }, [token, logout]);
};

// ============================================================================
// Data Scope by Role
// ============================================================================

export const DATA_SCOPE_CONFIG = {
  app_admin: {
    description: 'All data across all insurers, suppliers, and workshops',
    filters: null,
  },
  shareholder: {
    description: 'All data (read-only executive view)',
    filters: null,
  },
  insurer_admin: {
    description: 'Data for assigned insurers only',
    filters: ['insurer_keys'],
  },
  insurer_analyst: {
    description: 'Read-only data for assigned insurers',
    filters: ['insurer_keys'],
  },
  supplier_user: {
    description: 'Own supplier data, related orders and claims only',
    filters: ['supplier_key'],
  },
  workshop_user: {
    description: 'Own workshop data, related orders and claims only',
    filters: ['workshop_keys'],
  },
};

// ============================================================================
// Protected Route Component
// ============================================================================

export const ProtectedRoute: React.FC<{
  pageCode: string;
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ pageCode, children, fallback }) => {
  const { hasPageAccess, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#3a7de4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!hasPageAccess(pageCode)) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 mb-4 text-gray-300">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('errors.accessDenied')}</h2>
        <p className="text-gray-500">{t('errors.noPermission')}</p>
      </div>
    );
  }

  return <>{children}</>;
};